import { Link } from "react-router-dom";
import { Badge, Card, Text } from "@marekl/adminer/components";
import { pluralizeCzech } from "@marekl/adminer/format";
import type { Team } from "../../../lib/queries/teams";
import { teamStateBadges } from "../../../lib/memberState";
import styles from "./DashboardPage.module.css";

// „Náš tým" — the readiness card a vedoucí opens the dashboard for.
//
// Every value here already ships on /me/teams (fillState / fillPaymentStatus /
// fillMembersMissingCmfID), so this costs no extra request. The Czech comes
// from lib/memberState.ts — never inline it, or the roster and the dashboard
// start disagreeing about what „Připraveno" means.

const TEAM_STATUS_LABELS: Record<Team["status"], string> = {
  pending: "Čeká na schválení",
  active: "Aktivní",
  suspended: "Pozastavený",
};

const TEAM_STATUS_VARIANT: Record<Team["status"], "ok" | "warn" | "muted"> = {
  pending: "warn",
  active: "ok",
  suspended: "muted",
};

export interface TeamReadinessCardProps {
  team: Team;
  /** Whether the roster may still be written — mirrors the backend gate. */
  canRegister: boolean;
}

export function TeamReadinessCard({ team, canRegister }: TeamReadinessCardProps) {
  // A missing flag reads as ČNSMS, the same default every other surface
  // takes: the field is unconditional on the wire, and getting it wrong
  // that way only shows a Czech club what it already owes.
  const teamIsCmf = team.federation_is_primary !== false;
  const registered = team.members_registered ?? 0;
  const withPhoto = team.members_with_photo ?? 0;
  const missingCmfID = team.members_missing_cmf_id ?? 0;

  return (
    <Card>
      <Card.Header
        title={team.name}
        subtitlePlacement="right"
        subtitle={
          <Badge variant={TEAM_STATUS_VARIANT[team.status]}>
            {TEAM_STATUS_LABELS[team.status]}
          </Badge>
        }
      />
      <Card.Content>
        {/* The condensed team state is the single surface for payment, photos and
            registration (README „Sledování stavu"). It says only what is
            outstanding, so there is deliberately no second „Zaplaceno" badge
            below repeating it. */}
        {team.status === "active" && teamIsCmf && (
          <div className={styles.badges}>
            {teamStateBadges(team.state_issues).map((b) => (
              <Badge key={b.label} variant={b.variant}>
                {b.label}
              </Badge>
            ))}
          </div>
        )}

        {/* Registrace, fotky a ČNSMS ID jsou mašinérie ČNSMS: klub mimo ni
            žádnou z nich nedluží, server pro něj `state_issues` neplní, a
            „Připraveno" nad nulami by byl verdikt, který nikdo nevynesl. */}
        {teamIsCmf && (
          <div className={styles.facts}>
            <div className={styles.fact}>
              <div className={styles.factLabel}>Registrovaní členové</div>
              <div className={styles.factValue}>{registered}</div>
            </div>
            <div className={styles.fact}>
              <div className={styles.factLabel}>Má fotku</div>
              <div className={styles.factValue}>
                {withPhoto} / {registered}
              </div>
            </div>
          </div>
        )}

        {teamIsCmf && missingCmfID > 0 && (
          <Text as="p" variant="muted">
            {`Chybí ČNSMS ID u ${missingCmfID} ${pluralizeCzech(missingCmfID, ["člena", "členů", "členů"])}.`}
          </Text>
        )}

        <div className={styles.cardActions}>
          <Link to={`/teams/${team.id}`} className={styles.action}>
            Soupiska
          </Link>
          {canRegister && teamIsCmf && (
            <Link to={`/teams/${team.id}/registrace`} className={styles.action}>
              Registrace do sezóny
            </Link>
          )}
          <Link to={`/teams/${team.id}/souteze`} className={styles.action}>
            Soutěže a přihlášky
          </Link>
        </div>
      </Card.Content>
    </Card>
  );
}
